import { useRef } from 'react';

import type { CategoryNavigationItem } from '#/components/layout/CategoryNavigation';
import { CategoryNavigation } from '#/components/layout/CategoryNavigation';
import type { HomepageContentView } from '#/content/homepage';
import { useElementVisibility } from '#/hooks/useElementVisibility';
import { cn } from '#/utils/cn';

import { DesktopHomepageNavigation } from './DesktopHomepageNavigation';
import { HomepageHero } from './HomepageHero';

interface HomepageNavigationProps {
  productTypes: CategoryNavigationItem[];
  sectors: CategoryNavigationItem[];
  cta?: CategoryNavigationItem;
  hero?: HomepageContentView;
}

export function HomepageNavigation({
  productTypes,
  sectors,
  cta,
  hero,
}: HomepageNavigationProps) {
  const heroShellRef = useRef<HTMLDivElement | null>(null);
  const showPinnedDesktopNavigation = useElementVisibility(heroShellRef, {
    isVisible: (entry) =>
      !entry.isIntersecting && entry.boundingClientRect.bottom <= 0,
  });

  return (
    <>
      {/* Mobile Category Navigation */}
      <CategoryNavigation
        productTypes={productTypes}
        sectors={sectors}
        cta={cta}
        mode="mobile"
        enableSearchPreview
        className="col-full lg:hidden"
      />

      {/* Pinned Desktop Category Navigation */}
      <div
        aria-hidden={!showPinnedDesktopNavigation}
        className={cn(
          'fixed inset-x-0 top-0 z-50 hidden transition-[opacity,transform,visibility] duration-200 ease-[cubic-bezier(0.2,0,0,1)] will-change-transform motion-reduce:transition-none lg:block',
          showPinnedDesktopNavigation
            ? 'pointer-events-auto visible opacity-100'
            : 'pointer-events-none invisible opacity-0',
        )}
        style={{
          transform: showPinnedDesktopNavigation
            ? 'translate3d(0, 0, 0)'
            : 'translate3d(0, calc(-100% - 12px), 0)',
        }}
      >
        <CategoryNavigation
          productTypes={productTypes}
          sectors={sectors}
          cta={cta}
          mode="desktop"
          pinned
          enableSearchPreview
          className="relative! top-auto!"
        />
      </div>

      {/* Homepage Hero Section */}
      <section
        ref={heroShellRef}
        data-homepage-hero-shell="true"
        aria-label="Market Data Center introduction"
        className="col-full from-steel-10 to-gray-10 bg-linear-to-b"
      >
        <div className="grid-cols-layout grid">
          <div className="col-feature pt-16 lg:pt-24">
            <DesktopHomepageNavigation
              productTypes={productTypes}
              sectors={sectors}
              cta={cta}
            />
            <HomepageHero
              title={hero?.heroTitle}
              description={hero?.heroDescription}
              trustSignals={hero?.trustSignals}
            />
          </div>
        </div>
      </section>
    </>
  );
}
